import type {
  StructuredFieldType,
  StructuredFieldValue,
  StructuredSampleResponseConfig,
  StructuredSectionLayout,
  TableColumn,
} from './structuredQuestionnaire';

export type FieldConfigActionType = 'EDIT' | 'RESET';

export interface FieldConfigAction {
  type: FieldConfigActionType;
  label: string;
  startIcon?: string;
}

export interface FieldConfigJson {
  columnSpan?: number;
  gridColumn?: string;
  placeholder?: string | null;
  format?: string;
  multiple?: boolean;
  supportedFileTypes?: string[];
  maxFileSizeInMB?: number;
  maxImageSizeInMB?: number;
  groupTitle?: string;
  title?: string | null;
  description?: string | null;
  options?: Array<{ label: string; value: string; fieldLabel?: string }>;
  tooltip?: string;
  infoTooltip?: string;
  dependsOn?: string;
  optionsByValue?: Record<string, Array<{ label: string; value: string; fieldLabel?: string }>>;
  showOthersField?: {
    selectedValueEquals: string;
    saveToFieldKey?: string;
    fieldKey?: string;
  };
  source?: string;
  columns?: TableColumn[];
  hiddenField?: boolean;
  isPrimaryFile?: boolean;
  fileGroupKey?: string;
  defaultCurrency?: string;
  enableSearch?: boolean;
  /** Field key of a SELECT_CURRENCY field; appends ` (CODE)` to labels/placeholders/titles. */
  showCurrency?: string;
  /** When true, the field is editable. */
  editable?: boolean;
  /** Maps extracted verification data onto this field when verification completes. */
  extractionKey?: string;
  /** When true, hidden fields are shown in edit mode after a verifiable file is uploaded. */
  showOnEdit?: boolean;
  action?: FieldConfigAction;
  documentVerification?: {
    enabled: boolean;
    type?: string;
  };
  screening?: Record<string, any>;
  adminOnly?: boolean;
  showDivider?: boolean;
  editMode?: {
    groupTitle?: string;
  };
}

export interface SectionConfigJson {
  layout?: StructuredSectionLayout;
  repeatable?: {
    enabled: boolean;
    itemLabel?: string | null;
    removable?: boolean;
    addButtonLabel?: string;
  };
  addFields?: {
    allowAddFields?: boolean;
    allowDeleteFields?: boolean;
  };
  sampleResponse?: StructuredSampleResponseConfig;
  title?: string;
  description?: string | null;
}

export interface FieldFollowUpIssueDto {
  followUpIssueId: string;
  issueId: string;
  screeningRunId?: string;
  label: string;
  message: string;
  hidden: boolean;
  resolveOnFileUpload: boolean;
  status: DueDiligenceIssueResolutionStatus;
  code: string;
  overridable: boolean;
  resolutionType: DueDiligenceIssueResolutionType;
  responseFormats: { type: string; metadataKey: string }[];
  resolvedBy: string | null;
}

export interface DueDiligenceFieldDto {
  id: string;
  key: string;
  type: StructuredFieldType;
  label: string | null;
  required: boolean;
  sortOrder: number;
  configJson: FieldConfigJson | null;
  value: StructuredFieldValue;
  followUpIssues?: FieldFollowUpIssueDto[];
}

export interface DueDiligenceSectionDto {
  id: string;
  key: string | null;
  label: string | null;
  configJson: SectionConfigJson | null;
  sortOrder: number;
  fields: DueDiligenceFieldDto[];
}

export interface FieldEntry {
  id?: string | null;
  key: string;
  type: StructuredFieldType;
  label?: string | null;
  required?: boolean;
  sortOrder?: number;
  prompt?: string | null;
  message?: string | null;
  configJson?: FieldConfigJson | null;
  value?: StructuredFieldValue;
}

export interface SectionEntry {
  id?: string | null;
  key: string;
  label?: string | null;
  configJson?: SectionConfigJson | null;
  sortOrder?: number;
  fields?: FieldEntry[];
}

export interface ReqSaveItemDto {
  itemId: string;
  sections?: SectionEntry[];
}

export enum DueDiligenceIssueResolutionStatus {
  PENDING_REVIEW = 'PENDING_REVIEW',
  PENDING_CLIENT = 'PENDING_CLIENT',
  RESOLVED = 'RESOLVED',
  FOLLOW_UP_SENT = 'FOLLOW_UP_SENT',
  DISMISSED = 'DISMISSED',
}

export enum DueDiligenceIssueResolutionType {
  NEW_FIELD = 'NEW_FIELD',
  EXISTING_FIELD = 'EXISTING_FIELD',
}

export enum DueDiligenceScreeningStatus {
  NOT_STARTED = 'NOT_STARTED',
  PENDING = 'PENDING',
  PROCESSING = 'PROCESSING',
  PASSED = 'PASSED',
  REQUIRES_ACTION = 'REQUIRES_ACTION',
  FAILED = 'FAILED',
  CANCELLED = 'CANCELLED',
}

export enum DueDiligenceItemScreeningStatus {
  PENDING = 'PENDING',
  PROCESSING = 'PROCESSING',
  PASSED = 'PASSED',
  PENDING_REVIEW = 'PENDING_REVIEW',
  PENDING_CLIENT = 'PENDING_CLIENT',
  NOT_STARTED = 'NOT_STARTED',
}

export interface NewFieldDefinition {
  key: string;
  type: string;
  label: string;
  required: boolean;
  configJson?: FieldConfigJson | null;
  prompt?: string;
  message?: string;
}

export interface ExistingFieldDefinition {
  fieldId?: string;
  attachmentId?: string;
  message: string;
  prompt: string;
}

export type FieldDefinition = NewFieldDefinition | ExistingFieldDefinition;

export interface DueDiligenceFollowUpIssueDto {
  id: string;
  issueId: string;
  code: string | null;
  severity: string;
  status: DueDiligenceIssueResolutionStatus;
  resolutionType: DueDiligenceIssueResolutionType;
  itemId: string;
  sectionId: string | null;
  fieldId: string | null;
  definition: FieldDefinition;
  message: string;
  prompt: string;
  overridable: boolean;
  hidden?: boolean;
  resolveOnFileUpload?: boolean;
}

export interface ScreeningItemDto {
  itemId: string;
  sectionName: string;
  screeningStatus: DueDiligenceItemScreeningStatus;
  issues: DueDiligenceFollowUpIssueDto[];
}

export interface DueDiligenceScreeningOverviewDto {
  status: DueDiligenceScreeningStatus;
  result: Record<string, unknown> | null;
  items: ScreeningItemDto[];
}

export interface ReqFollowUpIssueEntry {
  id: string;
  definition: FieldDefinition;
  message?: string;
}

export enum FollowUpIssueAction {
  SEND_TO_CLIENT = 'SEND_TO_CLIENT',
  OVERRIDE = 'OVERRIDE',
}

export interface ReqFollowUpIssueActionEntry {
  id: string;
  action: FollowUpIssueAction;
  definition?: FieldEntry;
}

export interface ReqFollowUpIssueActionsDto {
  issues: ReqFollowUpIssueActionEntry[];
}
